import { UseMutationOptions, useQueryClient } from 'react-query';
import produce from 'immer';

import {
  DeleteWebhookMutation,
  DeleteWebhookMutationVariables,
  GetWebhookListQuery,
  useGetWebhookListQuery,
} from 'api';
import { useNotify } from 'hooks';

type Mutation = DeleteWebhookMutation;
type MutationVariables = DeleteWebhookMutationVariables;
type QueryData = GetWebhookListQuery;

const useDeleteWebhook = () => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const queryKey = useGetWebhookListQuery.getKey();

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    onMutate: ({ webhook_id }) => {
      const baseState = queryClient.getQueryData<QueryData>(queryKey);
      const nextState =
        baseState &&
        produce(baseState, draftState => {
          const idx = draftState.getWebhookList.webhooks.findIndex(
            webhook => webhook.webhook_id === webhook_id
          );
          if (idx !== 1) {
            draftState.getWebhookList.webhooks.splice(idx, 1);
          }
        });

      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, nextState);
      }

      return baseState;
    },
    onError: (error, mutationArgs, baseState: QueryData) => {
      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, baseState);
      }
      notify.mutationError();
    },
    onSettled: () => {
      void queryClient.refetchQueries(queryKey);
    },
    onSuccess: () => {
      notify.info({ message: 'Webhook Deleted' });
    },
  };

  return mutationOptions;
};

export default useDeleteWebhook;
